// React Imports
import { useState, useEffect } from 'react'

// Type Imports
import type { ChildrenType, Direction, Mode, SystemMode } from '@core/types'
import type { Settings } from '@core/contexts/settingsContext'

// Context Imports
import { VerticalNavProvider } from '@menu/contexts/verticalNavContext'
import { SettingsProvider } from '@core/contexts/settingsContext'
import ThemeProvider from '@components/theme'
import { DataProvider } from '@/contexts/DataProvider'
import ReduxProvider from '@/redux-store/ReduxProvider'
import AppReactToastify from '@/libs/styles/AppReactToastify'

// Util Imports
import { getMode, getSettingsFromCookie, getSystemMode } from '@core/utils/clientHelpers'
import { CaslProvider } from '@/contexts/CaslProvider'

type Props = ChildrenType & {
  direction: Direction
}

const Providers = (props: Props) => {
  // Props
  const { children, direction } = props

  // States
  const [mode, setMode] = useState<Mode>('light')
  const [settingsCookie, setSettingsCookie] = useState<Settings>({})
  const [systemMode, setSystemMode] = useState<SystemMode>('light')

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const modeValue = await getMode()
        const settingsValue = getSettingsFromCookie()
        const systemModeValue = await getSystemMode()

        setMode(modeValue)
        setSettingsCookie(settingsValue)
        setSystemMode(systemModeValue)
      } catch (error) {
        console.error('Error loading settings:', error)
      } finally {}
    }

    loadSettings()
  }, [])

  return (
    <VerticalNavProvider>
      <SettingsProvider settingsCookie={settingsCookie} mode={mode}>
        <ThemeProvider direction={direction} systemMode={systemMode}>
          <DataProvider>
            <ReduxProvider>
              <CaslProvider>{children}</CaslProvider>
            </ReduxProvider>
          </DataProvider>
          <AppReactToastify direction={direction} hideProgressBar />
        </ThemeProvider>
      </SettingsProvider>
    </VerticalNavProvider>
  )
}

export default Providers
